import React, { useState } from 'react';
import { format } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import { Button } from './ui/button';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { cn } from '@/lib/utils';

// Preset time range options
export const TIME_RANGE_PRESETS = {
  '24h': { label: 'Today', hours: 24 },
  '7d': { label: 'Last 7 days', hours: 168 },
  '30d': { label: 'Last 30 days', hours: 720 },
  'custom': { label: 'Custom range', hours: null },
};

export const TimeRangeSelector = ({ value, onChange }) => {
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  // value shape: { preset: '24h' | '7d' | '30d' | 'custom', startDate?: Date, endDate?: Date }
  const { preset, startDate, endDate } = value;

  const handlePresetChange = (newPreset) => {
    if (newPreset === 'custom') {
      // Open calendar for custom selection
      setIsCalendarOpen(true);
      onChange({ preset: 'custom', startDate: null, endDate: null });
    } else {
      onChange({ preset: newPreset, startDate: null, endDate: null });
    }
  };

  const handleDateRangeSelect = (range) => {
    if (range?.from && range?.to) {
      onChange({
        preset: 'custom',
        startDate: range.from,
        endDate: range.to,
      });
    } else if (range?.from) {
      onChange({
        preset: 'custom',
        startDate: range.from,
        endDate: null,
      });
    }
  };

  const getDisplayLabel = () => {
    if (preset === 'custom' && startDate && endDate) {
      return `${format(startDate, 'MMM d')} - ${format(endDate, 'MMM d, yyyy')}`;
    }
    if (preset === 'custom' && startDate) {
      return `${format(startDate, 'MMM d, yyyy')} - ...`;
    }
    return TIME_RANGE_PRESETS[preset]?.label || 'Select range';
  };

  return (
    <div className="flex items-center gap-2">
      <Select value={preset} onValueChange={handlePresetChange}>
        <SelectTrigger className="w-[160px]">
          <SelectValue placeholder="Select range">
            {preset !== 'custom' ? TIME_RANGE_PRESETS[preset]?.label : 'Custom range'}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="24h">Today</SelectItem>
          <SelectItem value="7d">Last 7 days</SelectItem>
          <SelectItem value="30d">Last 30 days</SelectItem>
          <SelectItem value="custom">Custom range</SelectItem>
        </SelectContent>
      </Select>

      {preset === 'custom' && (
        <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                'w-[240px] justify-start text-left font-normal',
                !startDate && 'text-muted-foreground'
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {startDate && endDate ? (
                <>
                  {format(startDate, 'MMM d')} - {format(endDate, 'MMM d, yyyy')}
                </>
              ) : startDate ? (
                <>
                  {format(startDate, 'MMM d, yyyy')} - Select end
                </>
              ) : (
                'Pick date range'
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="range"
              selected={{ from: startDate, to: endDate }}
              onSelect={handleDateRangeSelect}
              numberOfMonths={2}
              disabled={{ after: new Date() }}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};

// Helper to convert time range value to API params
export const getTimeRangeParams = (timeRange) => {
  const { preset, startDate, endDate } = timeRange;

  if (preset === 'custom' && startDate && endDate) {
    return {
      start_date: startDate.toISOString(),
      end_date: endDate.toISOString(),
    };
  }

  // Use hours for preset ranges
  const hours = TIME_RANGE_PRESETS[preset]?.hours || 168;
  return { hours };
};
